import React from 'react';
import {
  GridActionsCellItem,
  GridAlignment,
  GridColDef,
  GridColumnHeaderParams,
  GridPreProcessEditCellProps,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridRowModes,
  GridRowModesModel,
  GridValueFormatterParams,
  // GridValueFormatterParams,
} from '@mui/x-data-grid-pro';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CancelIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@eposnow/ui-core';
import { useTheme } from '@mui/material/styles';
import { Category, CurrencyInfo } from '../../../../types';
import GenericGridContent from '../../../../components/datagridComponents/GenericGridContent';
import GridCellEdit from '../../../../components/datagridComponents/GridCellEdit';
import GridCellEditTextDropdown from '../../../../components/datagridComponents/GridCellEditTextDropdown';
import {
  descriptionValidationSchema,
  nameValidationSchema,
  priceValidationSchema,
} from './validationSchema';

const BuildColumns = (
  rowModesModel: GridRowModesModel,
  handleSaveClick: (id: number) => () => void,
  handleCancelClick: (id: number) => () => void,
  handleEditClick: (id: number) => () => void,
  handleDeleteClick: (id: number) => () => void,
  handleAddClick: (id: number) => () => void,
  handleDuplicateClick: (id: number) => () => void,
  categories: Category[],
  currencyInfo: CurrencyInfo
): GridColDef[] => {
  const { t } = useTranslation();
  const theme = useTheme();

  return [
    {
      field: 'name',
      sortable: false,
      editable: true,
      flex: 1,
      minWidth: 350,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) =>
        nameValidationSchema
          .isValid({
            name: params?.props?.value,
          })
          .then((valid) => ({ ...params.props, error: !valid }))
          .catch(() => ({ ...params.props })),
      renderHeader: (_params: GridColumnHeaderParams) => (
        <GenericGridContent
          content={[
            {
              text: t('screens.products.sidekick.name'),
              shouldTranslate: true,
              typographyProps: {
                variant: 'boldText',
              },
            },
          ]}
        />
      ),
      renderCell: (params: GridRenderCellParams) => (
        <GenericGridContent
          content={[
            {
              text: params.value,
              shouldTranslate: false,
              shouldClipContent: true,
              typographyProps: {
                variant: 'body2',
              },
            },
          ]}
        />
      ),
      renderEditCell: (params: GridRenderEditCellParams) => (
        <GridCellEdit
          gridRenderParams={params}
          labelKey={t('screens.products.sidekick.name')}
          validationSchema={nameValidationSchema}
        />
      ),
    },
    {
      field: 'description',
      sortable: false,
      minWidth: 450,
      editable: true,
      flex: 1,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) =>
        descriptionValidationSchema
          .isValid({
            description: params?.props?.value,
          })
          .then((valid) => ({ ...params.props, error: !valid }))
          .catch(() => ({ ...params.props })),
      renderHeader: (_params: GridColumnHeaderParams) => (
        <GenericGridContent
          content={[
            {
              text: t('screens.products.sidekick.description'),
              shouldTranslate: true,
              typographyProps: {
                variant: 'boldText',
              },
            },
          ]}
        />
      ),
      renderCell: (params: GridRenderCellParams) => (
        <GenericGridContent
          content={[
            {
              text: params.value,
              shouldTranslate: false,
              shouldClipContent: true,
              typographyProps: {
                variant: 'body2',
              },
            },
          ]}
        />
      ),
      renderEditCell: (params: GridRenderEditCellParams) => (
        <GridCellEdit
          gridRenderParams={params}
          labelKey={t('screens.products.sidekick.description')}
          validationSchema={descriptionValidationSchema}
        />
      ),
    },
    {
      field: 'categoryId',
      headerName: 'category',
      editable: true,
      sortable: false,
      flex: 1,
      minWidth: 100,
      valueFormatter: (params: GridValueFormatterParams) => {
        const categoryName =
          categories?.find((category) => category.Id === params?.value)?.Name || 'Top Level';
        return categoryName;
      },
      renderHeader: (_params: GridColumnHeaderParams) => (
        <GenericGridContent
          content={[
            {
              text: t('screens.products.sidekick.category'),
              shouldTranslate: true,
              typographyProps: {
                variant: 'boldText',
              },
            },
          ]}
        />
      ),
      renderCell: (params: GridRenderCellParams) => {
        const categoryName =
          categories?.find((category) => category.Id === params?.value)?.Name || 'Top Level';

        return (
          <GenericGridContent
            content={[
              {
                text: categoryName,
                shouldTranslate: false,
                typographyProps: {
                  variant: 'body2',
                },
              },
            ]}
          />
        );
      },
      renderEditCell: (params: GridRenderEditCellParams) => {
        const categoryOptions = categories?.map((row) => ({
          label: row.Name,
          value: row.Id?.toString(),
        }));

        return (
          <GridCellEditTextDropdown
            gridRenderParams={params}
            initialChoiceValue={params.row.categoryId}
            options={categoryOptions}
            setValueInsteadOfLabel
          />
        );
      },
    },
    {
      field: 'price',
      sortable: false,
      editable: true,
      minWidth: 100,
      flex: 1,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) =>
        priceValidationSchema
          .isValid({
            price: params?.props?.value,
          })
          .then((valid) => ({ ...params.props, error: !valid }))
          .catch(() => ({ ...params.props })),
      renderHeader: (_params: GridColumnHeaderParams) => (
        <GenericGridContent
          content={[
            {
              text: t('screens.products.sidekick.price'),
              shouldTranslate: true,
              typographyProps: {
                variant: 'boldText',
              },
            },
          ]}
        />
      ),
      renderCell: (params: GridRenderCellParams) => (
        <GenericGridContent
          content={[
            {
              text: `${currencyInfo?.currencySymbol || '£'}${params.value}`,
              shouldTranslate: false,
              shouldClipContent: true,
              typographyProps: {
                variant: 'body2',
              },
            },
          ]}
        />
      ),
      renderEditCell: (params: GridRenderEditCellParams) => (
        <GridCellEdit
          gridRenderParams={params}
          labelKey={t('screens.products.sidekick.price')}
          type="number"
          min={0}
          max={10000}
          step={0.01}
          validationSchema={priceValidationSchema}
        />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      minWidth: 200,
      flex: 1,
      renderHeader: (_params: GridColumnHeaderParams) => (
        <GenericGridContent
          content={[
            {
              text: 'common.actions',
              shouldTranslate: true,
              typographyProps: {
                variant: 'boldText',
              },
            },
          ]}
        />
      ),
      align: 'right' as GridAlignment,
      headerAlign: 'right' as GridAlignment,
      hideSortIcons: true,
      headerClassName: 'hideRightSeparator',
      getActions: ({ id }) => {
        const newId = id as number;
        const isInEditMode = rowModesModel[newId]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <Tooltip title={t('actions.save')} theme={theme}>
              <GridActionsCellItem
                icon={<SaveIcon />}
                label={t('actions.save')}
                onClick={handleSaveClick(newId)}
                color="inherit"
              />
            </Tooltip>,
            <Tooltip title={t('actions.cancel')} theme={theme}>
              <GridActionsCellItem
                icon={<CancelIcon />}
                label={t('actions.cancel')}
                onClick={handleCancelClick(newId)}
                color="inherit"
              />
            </Tooltip>,
          ];
        }

        return [
          <Tooltip title={t('actions.add')} theme={theme}>
            <GridActionsCellItem
              icon={<AddIcon />}
              label={t('actions.add')}
              onClick={handleAddClick(newId)}
              color="inherit"
            />
          </Tooltip>,
          <Tooltip title={t('actions.duplicate')} theme={theme}>
            <GridActionsCellItem
              icon={<ContentCopyIcon />}
              label={t('actions.duplicate')}
              onClick={handleDuplicateClick(newId)}
              color="inherit"
            />
          </Tooltip>,
          <Tooltip title={t('actions.edit')} theme={theme}>
            <GridActionsCellItem
              icon={<EditIcon />}
              label={t('actions.edit')}
              onClick={handleEditClick(newId)}
              color="inherit"
            />
          </Tooltip>,
          <Tooltip title={t('actions.delete')} theme={theme}>
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label={t('actions.delete')}
              onClick={handleDeleteClick(newId)}
              color="inherit"
            />
          </Tooltip>,
        ];
      },
    },
  ];
};

export default BuildColumns;
